import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { parseDeepLink, SUPPORTED_DEEP_LINK_ENTITY_TYPES } from "@/helpers/deep-link";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function ShareRedirect(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<"loading" | "brokenLink" | "wrongProject" | "wrongEntity">("loading");

  const selfAccountQueries = useSelfAccountQueries();
  const { data: linkedProjects, isPending: isPendingLinkedProjects } = useQuery(
    selfAccountQueries.listLinkedProjects(),
  );

  const deepLink = useMemo(
    // Host doesn't matter for parsing, so we can use any valid host here.
    () => parseDeepLink("https://localhost" + location.pathname + location.search + location.hash),
    [location.pathname, location.search, location.hash],
  );

  useEffect(() => {
    void (() => {
      if (!deepLink || !SUPPORTED_DEEP_LINK_ENTITY_TYPES.includes(deepLink.entityType)) {
        setState("brokenLink");

        return;
      }

      if (!linkedProjects) {
        return;
      }

      const project = linkedProjects.find((x) => x.id === deepLink.projectId);
      if (!project) {
        setState("wrongProject");

        return;
      }

      const slug = project.slug;
      switch (deepLink.entityType) {
        case "message":
          return navigate(routes.communityFeed.postDetails({ slug, id: deepLink.entityId }));
        case "ticket":
          return navigate(routes.tickets.details({ slug, id: deepLink.entityId }));
        case "survey":
          return navigate(routes.surveys.respond({ slug, id: deepLink.entityId }));
        case "event":
          return navigate(routes.events.details({ slug, id: deepLink.entityId }));
        case "poll":
          return navigate(routes.communityFeed.pollDetails({ slug, id: deepLink.entityId }));
        case "service":
          return navigate(routes.servicePartners.details({ slug, id: deepLink.entityId }));
        case "document": {
          if (!deepLink.folderId) {
            setState("brokenLink");

            return;
          }

          return navigate(routes.documents.documentsList({ slug, folderId: deepLink.folderId }));
        }
        case "group":
          return navigate(routes.interestGroups.details({ slug, id: deepLink.entityId }));
        case "bookableAsset":
          return navigate(routes.bookings.bookAsset({ slug, aid: deepLink.entityId }));
        default:
          setState("wrongEntity");
      }
    })();
  }, [deepLink, navigate, linkedProjects]);

  if (isPendingLinkedProjects || state === "loading") {
    return null;
  }

  if (state === "brokenLink" || state === "wrongEntity") {
    return <ErrorPage status={404} />;
  }

  if (state === "wrongProject") {
    return <ErrorPage status={403} extraInfo={t("page.share-redirect.no-access-to-project")} />;
  }

  return null;
}
